import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import {
  AS_FORMULAS,
  AS_HUB_MODULES,
  buildProfileSummary,
  computeGlobalProgress,
  computeProfileScore,
  currentHubModule,
  daysUntilLabel,
  emptyLikertAnswers,
  emptyOpenAnswers,
  ENTREPRENEURIAL_PROFILE_DIMENSIONS,
  formulaByKey,
  likertComplete,
  activitiesContentAvailable,
  evaluationContentAvailable,
  moduleUnlocked,
  openAnswersComplete,
  PROFILE_LEVEL_LABELS,
  PROFILE_OPEN_QUESTIONS,
  PROFILE_SYNTHESIS_COMPETENCIES,
  profileLevelFromScore,
  profileStrengthsAndGaps,
  synthesisCompetencyAverage,
  resolveNextStep,
  type AccompanimentActivity,
  type AsFormulaKey,
  type AsHubModule,
  type LikertAnswers,
  type RoadmapObjective,
  type StrategicAccompaniment,
  type TextAnswers,
} from '@isbd/shared';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth/AuthProvider';
import { ActivitiesPanel } from '@/features/accompagnement/ActivitiesPanel';
import { RoadmapPanel } from '@/features/accompagnement/RoadmapPanel';
import { useToast } from '@/features/ui/toast/useToast';
import '@/features/accompagnement/accompagnement.css';

type AccRow = StrategicAccompaniment & {
  advisor?: { first_name: string; last_name: string; email: string; avatar_url?: string | null } | null;
};

function ProgressRing({
  value,
  color = '#22c55e',
  size = 'sm',
}: {
  value: number;
  color?: string;
  size?: 'sm' | 'lg';
}) {
  const r = size === 'lg' ? 48 : 26;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, value));
  const offset = c - (pct / 100) * c;
  const vb = size === 'lg' ? 120 : 64;
  const cx = vb / 2;
  return (
    <svg className={size === 'lg' ? 'as-ring as-ring-lg' : 'as-ring'} viewBox={`0 0 ${vb} ${vb}`} aria-hidden>
      <circle cx={cx} cy={cx} r={r} fill="none" stroke="#e5eef6" strokeWidth={size === 'lg' ? 10 : 6} />
      <circle
        cx={cx}
        cy={cx}
        r={r}
        fill="none"
        stroke={color}
        strokeWidth={size === 'lg' ? 10 : 6}
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={offset}
        transform={`rotate(-90 ${cx} ${cx})`}
      />
      <text
        x={cx}
        y={cx + (size === 'lg' ? 6 : 4)}
        textAnchor="middle"
        className={size === 'lg' ? 'as-ring-text as-ring-text-lg' : 'as-ring-text'}
      >
        {pct}%
      </text>
    </svg>
  );
}

function StarRating({
  value,
  onChange,
  disabled,
}: {
  value: number;
  onChange?: (n: number) => void;
  disabled?: boolean;
}) {
  const filled = Math.round(value);
  return (
    <div className="as-stars" role="img" aria-label={`${value} sur 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          className={`as-star ${filled >= n ? 'is-on' : ''}`}
          disabled={disabled || !onChange}
          onClick={() => onChange?.(n)}
          aria-label={`${n} étoile${n > 1 ? 's' : ''}`}
        >
          <svg viewBox="0 0 24 24" aria-hidden>
            <path
              fill="currentColor"
              d="M12 2.5l2.9 5.9 6.5.9-4.7 4.6 1.1 6.5L12 17.8 6.2 20.4l1.1-6.5L2.6 9.3l6.5-.9L12 2.5z"
            />
          </svg>
        </button>
      ))}
    </div>
  );
}

function ModuleIcon({ tone }: { tone: string }) {
  const paths: Record<string, ReactElement> = {
    blue: (
      <path
        fill="currentColor"
        d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4Zm0 2c-3.3 0-10 1.7-10 5v1h20v-1c0-3.3-6.7-5-10-5Z"
        transform="translate(12 12) scale(1)"
      />
    ),
    green: (
      <path
        fill="currentColor"
        d="M8.5 14.5 5 11l1.4-1.4 2.1 2.1 5-5L15 8.1l-6.5 6.4ZM7 18h10v2H7v-2Zm2-14h6v2H9V4Z"
        transform="translate(10 8)"
      />
    ),
    orange: (
      <path
        fill="currentColor"
        d="M12 4 4 8v8l8 4 8-4V8l-8-4Zm0 2.2 5.2 2.6L12 11.4 6.8 8.8 12 6.2ZM6 10.3l5 2.5v6.4l-5-2.5v-6.4Zm12 0v6.4l-5 2.5v-6.4l5-2.5Z"
        transform="translate(8 6)"
      />
    ),
    navy: (
      <path
        fill="currentColor"
        d="M8 4h8a2 2 0 0 1 2 2v14l-6-3-6 3V6a2 2 0 0 1 2-2Zm0 2v11.1l4-2 4 2V6H8Z"
        transform="translate(10 6)"
      />
    ),
    teal: (
      <path
        fill="currentColor"
        d="M5 17h2v-5H5v5Zm4 0h2V8H9v9Zm4 0h2v-7h-2v7Zm4 0h2V5h-2v12ZM4 19h16v2H4v-2Z"
        transform="translate(8 6)"
      />
    ),
  };
  return (
    <svg width="22" height="22" viewBox="0 0 40 40" aria-hidden>
      {paths[tone] ?? paths.blue}
    </svg>
  );
}

function initials(first?: string | null, last?: string | null, email?: string | null) {
  const a = (first || '').trim().charAt(0);
  const b = (last || '').trim().charAt(0);
  if (a || b) return `${a}${b}`.toUpperCase();
  return (email || '?').slice(0, 2).toUpperCase();
}

function formatDateFr(iso: string | null | undefined) {
  if (!iso) return '—';
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
}

export function AccompagnementPage() {
  const { profile } = useAuth();
  const toast = useToast();
  const [acc, setAcc] = useState<AccRow | null>(null);
  const [activities, setActivities] = useState<AccompanimentActivity[]>([]);
  const [view, setView] = useState<'hub' | AsHubModule>('hub');
  const [likert, setLikert] = useState<LikertAnswers>(() => emptyLikertAnswers());
  const [open, setOpen] = useState<TextAnswers>(() => emptyOpenAnswers());
  const [formulaKey, setFormulaKey] = useState<AsFormulaKey | ''>('');
  const [expectedResults, setExpectedResults] = useState('');
  const [engageAck, setEngageAck] = useState(false);
  const [loading, setLoading] = useState(false);
  const [expandedDim, setExpandedDim] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!profile) return;
    const { data, error: err } = await supabase
      .from('strategic_accompagnements')
      .select('*')
      .eq('user_id', profile.id)
      .maybeSingle();
    if (err) {
      toast.error(err.message);
      return;
    }
    if (!data) {
      setAcc(null);
      setActivities([]);
      return;
    }
    const row = data as AccRow;
    setLikert({ ...emptyLikertAnswers(), ...(row.likert_answers || {}) });
    setOpen({ ...emptyOpenAnswers(), ...(row.open_answers || {}) });
    setEngageAck(Boolean(row.engagement_ack));
    setFormulaKey((row.formula_key as AsFormulaKey) || '');
    setExpectedResults(row.expected_results || '');

    if (row.advisor_id) {
      const { data: adv, error: advErr } = await supabase
        .from('profiles')
        .select('first_name, last_name, email, avatar_url')
        .eq('id', row.advisor_id)
        .maybeSingle();
      if (advErr) toast.error(advErr.message);
      else row.advisor = adv;
    }
    setAcc(row);

    const { data: acts } = await supabase
      .from('accompaniment_activities')
      .select('*')
      .eq('accompaniment_id', row.id)
      .order('sort_order', { ascending: true });
    setActivities((acts ?? []) as AccompanimentActivity[]);
  }, [profile, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const displayName = useMemo(() => {
    const n = [profile?.first_name, profile?.last_name].filter(Boolean).join(' ');
    return n || profile?.email || 'Entrepreneur';
  }, [profile]);

  const welcomeName = useMemo(() => {
    const first = profile?.first_name?.trim();
    if (first) return first;
    const last = profile?.last_name?.trim();
    if (last) return last;
    return displayName;
  }, [profile, displayName]);

  const formula = formulaByKey(acc?.formula_key);
  const globalProgress = computeGlobalProgress(acc?.status ?? null, activities);
  const liveScore = computeProfileScore(likert);
  const profileScore = Math.round(Number(acc?.profile_score) || liveScore || 0);
  const profileLabel = acc?.profile_level
    ? PROFILE_LEVEL_LABELS[acc.profile_level]
    : liveScore
      ? PROFILE_LEVEL_LABELS[profileLevelFromScore(liveScore)]
      : 'Non évalué';
  const activeModule = currentHubModule(acc?.status ?? null);
  const activitiesReady = activitiesContentAvailable(acc?.status ?? null, acc?.roadmap_validated_at);
  const evaluationReady = evaluationContentAvailable(acc?.status ?? null);
  const next = resolveNextStep(acc?.status ?? null, activities, (acc?.roadmap ?? []) as RoadmapObjective[]);
  const roadmap = (acc?.roadmap ?? []) as RoadmapObjective[];
  const { strengths, gaps } = useMemo(() => profileStrengthsAndGaps(likert), [likert]);
  const profilLocked = Boolean(acc && acc.status !== 'profiling');

  async function ensureStarted() {
    if (!profile) return null;
    if (acc) return acc;
    const { data, error: err } = await supabase
      .from('strategic_accompagnements')
      .insert({ user_id: profile.id, status: 'profiling' })
      .select('*')
      .single();
    if (err) throw err;
    await load();
    return data as AccRow;
  }

  async function openModule(mod: AsHubModule) {
    if (!acc && mod === 'profil') {
      setLoading(true);
      try {
        await ensureStarted();
        setView('profil');
      } catch (e) {
        toast.error(e instanceof Error ? e.message : 'Impossible de démarrer');
      } finally {
        setLoading(false);
      }
      return;
    }
    if (!moduleUnlocked(mod, acc?.status ?? null) && mod !== activeModule) {
      // Activités & évaluation restent toujours ouverts (contenu adapté au statut).
      if (mod !== 'activites' && mod !== 'evaluation') {
        toast.error('Cette étape n’est pas encore accessible.');
        return;
      }
    }
    setView(mod);
  }

  function setDimensionScore(dimKey: string, n: number) {
    const dim = ENTREPRENEURIAL_PROFILE_DIMENSIONS.find((d) => d.key === dimKey);
    if (!dim) return;
    const nextLikert = { ...likert };
    for (const q of dim.questions) nextLikert[q.id] = n;
    setLikert(nextLikert);
  }

  async function submitProfile() {
    if (!likertComplete(likert)) {
      toast.error('Évaluez les 7 compétences (1 à 5 étoiles chacune).');
      return;
    }
    if (!openAnswersComplete(open)) {
      toast.error('Complétez les informations générales (8 caractères min.).');
      return;
    }
    setLoading(true);
    try {
      const current = await ensureStarted();
      if (!current) throw new Error('Parcours introuvable');
      const score = computeProfileScore(likert);
      const level = profileLevelFromScore(score);
      const summary = buildProfileSummary(score, level, open);
      const { error: err } = await supabase
        .from('strategic_accompagnements')
        .update({
          likert_answers: likert,
          open_answers: open,
          profile_score: score,
          profile_level: level,
          profile_summary: summary,
          profile_completed_at: new Date().toISOString(),
          status: 'ready_to_engage',
        })
        .eq('id', current.id);
      if (err) throw err;
      toast.success(`Profil évalué — ${PROFILE_LEVEL_LABELS[level]} (${score}%).`, 'Accompagnement');
      await load();
      setView('hub');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Enregistrement impossible');
    } finally {
      setLoading(false);
    }
  }

  async function confirmEngagement() {
    if (!acc) return;
    if (!formulaKey) {
      toast.error('Choisissez une formule d’accompagnement.');
      return;
    }
    if (expectedResults.trim().length < 3) {
      toast.error('Choisissez une formule pour définir les résultats attendus.');
      return;
    }
    if (!engageAck) {
      toast.error('Cochez la case pour confirmer votre engagement formel.');
      return;
    }
    setLoading(true);
    try {
      const { error: err } = await supabase
        .from('strategic_accompagnements')
        .update({
          engagement_ack: true,
          engaged_at: new Date().toISOString(),
          formula_key: formulaKey,
          expected_results: expectedResults.trim(),
          status: 'engaged',
        })
        .eq('id', acc.id);
      if (err) throw err;
      toast.success('Engagement acté. Un conseiller vous sera assigné.', 'Accompagnement');
      await load();
      setView('hub');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Engagement impossible');
    } finally {
      setLoading(false);
    }
  }

  const coachName = acc?.advisor
    ? [acc.advisor.first_name, acc.advisor.last_name].filter(Boolean).join(' ') || acc.advisor.email
    : null;

  return (
    <div className="as-page">
      {view === 'hub' ? (
        <>
          <div>
            <p className="as-welcome">Bonjour, {welcomeName}</p>
            <p className="as-welcome-sub">Votre parcours d’accompagnement stratégique</p>
          </div>

          <div className="as-kpi-grid">
            <article className="as-kpi">
              <p className="as-kpi-label">Niveau entrepreneurial</p>
              <div className="as-kpi-body">
                <ProgressRing value={acc?.profile_score != null ? profileScore : 0} />
                <div>
                  <p className="as-kpi-meta" style={{ margin: 0, fontWeight: 700, color: 'var(--ink)' }}>
                    {acc?.profile_score != null ? profileLabel : 'À évaluer'}
                  </p>
                </div>
              </div>
            </article>

            <article className="as-kpi">
              <p className="as-kpi-label">Formule choisie</p>
              <p className="as-kpi-value">{formula?.label ?? '—'}</p>
              <p className={`as-kpi-meta ${formula ? 'is-accent' : ''}`}>
                {formula?.duration ?? 'Non sélectionnée'}
              </p>
            </article>

            <article className="as-kpi">
              <p className="as-kpi-label">Progression globale</p>
              <p className="as-kpi-value">{globalProgress}%</p>
              <div className="as-progress-track" aria-hidden>
                <div className="as-progress-fill" style={{ width: `${globalProgress}%` }} />
              </div>
            </article>

            <article className="as-kpi">
              <p className="as-kpi-label">Coach attribué</p>
              {coachName ? (
                <div className="as-coach">
                  <div className="as-avatar">
                    {acc?.advisor?.avatar_url ? (
                      <img src={acc.advisor.avatar_url} alt="" />
                    ) : (
                      initials(acc?.advisor?.first_name, acc?.advisor?.last_name, acc?.advisor?.email)
                    )}
                  </div>
                  <div>
                    <p className="as-coach-name">{coachName}</p>
                    <p className="as-coach-role">Coach Senior</p>
                    <button type="button" className="as-link" onClick={() => setView('feuille')}>
                      Voir le profil
                    </button>
                  </div>
                </div>
              ) : (
                <p className="as-kpi-meta" style={{ marginTop: '0.5rem' }}>
                  En attente d’assignation
                </p>
              )}
            </article>
          </div>

          <div>
            <h3 className="as-section-title">Aperçu de votre accompagnement</h3>
            <div className="as-modules">
              {AS_HUB_MODULES.map((m) => {
                const alwaysOpen = m.key === 'activites' || m.key === 'evaluation';
                const unlocked =
                  alwaysOpen ||
                  moduleUnlocked(m.key, acc?.status ?? null) ||
                  (!acc && m.key === 'profil');
                const isActive = activeModule === m.key;
                return (
                  <article
                    key={m.key}
                    className={`as-module ${isActive ? 'is-active' : ''} ${unlocked ? '' : 'is-locked'}`}
                  >
                    <div className={`as-module-icon ${m.tone}`}>
                      <ModuleIcon tone={m.tone} />
                    </div>
                    <h3>{m.title}</h3>
                    <p>{m.description}</p>
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={loading || (!unlocked && m.key !== 'profil')}
                      onClick={() => void openModule(m.key)}
                    >
                      Voir
                    </button>
                  </article>
                );
              })}
            </div>
          </div>

          <section className="as-next">
            <div>
              <p className="as-next-label">Prochaine étape</p>
              <p className="as-next-title">{next.title}</p>
              <p className="as-next-desc">{next.description}</p>
            </div>
            <div className="as-next-due">
              <p className="as-next-label">Échéance</p>
              <div className="as-next-due-row">
                <span className="as-cal-icon" aria-hidden>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M7 2h2v2h6V2h2v2h3a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h3V2zm12 8H5v10h14V10zM5 8h14V6H5v2z" />
                  </svg>
                </span>
                <strong>{formatDateFr(next.dueDate)}</strong>
              </div>
              <span>{daysUntilLabel(next.dueDate) ?? 'Selon votre rythme'}</span>
            </div>
            <div>
              <p className="as-next-label">Avancement de l’étape</p>
              <p className="as-kpi-value" style={{ fontSize: '1.25rem' }}>
                {next.progress}%
              </p>
              <div className="as-progress-track" aria-hidden>
                <div className="as-progress-fill" style={{ width: `${next.progress}%` }} />
              </div>
            </div>
            <button
              type="button"
              className="btn btn-primary"
              disabled={loading}
              onClick={() => void openModule(next.module)}
            >
              Continuer
            </button>
          </section>
        </>
      ) : null}

      {view !== 'hub' ? (
        <div className="as-detail">
          <div className="as-detail-head">
            <button type="button" className="btn btn-ghost" onClick={() => setView('hub')}>
              ← Retour au parcours
            </button>
          </div>

          {view === 'profil' ? (
            <div>
              <div className="as-profil-hero">
                <div>
                  <h3>Examen du Profil Entrepreneurial</h3>
                  <p className="muted" style={{ margin: 0 }}>
                    Évaluez vos compétences et votre potentiel entrepreneurial
                  </p>
                </div>
                <div className="as-profil-illus" aria-hidden>
                  <svg width="56" height="56" viewBox="0 0 64 64" fill="none">
                    <rect x="28" y="10" width="28" height="40" rx="4" fill="#2f6fed" opacity="0.2" />
                    <rect x="32" y="16" width="20" height="28" rx="2" fill="#2f6fed" opacity="0.45" />
                    <circle cx="18" cy="38" r="8" fill="#1a3c5e" opacity="0.85" />
                    <circle cx="42" cy="42" r="7" fill="#4a7eb0" opacity="0.9" />
                  </svg>
                </div>
              </div>

              <div className="as-profil-grid">
                <section className="as-profil-col">
                  <h4>Informations générales</h4>
                  {PROFILE_OPEN_QUESTIONS.map((q) => (
                    <div className="as-profil-field" key={q.id}>
                      <label htmlFor={`open-${q.id}`}>{q.label}</label>
                      <textarea
                        id={`open-${q.id}`}
                        placeholder={q.placeholder}
                        value={open[q.id] ?? ''}
                        disabled={profilLocked}
                        onChange={(e) => setOpen({ ...open, [q.id]: e.target.value })}
                      />
                    </div>
                  ))}
                </section>

                <section className="as-profil-col">
                  <h4>7 compétences clés</h4>
                  <p className="as-profil-likert-note">
                    Notez chaque compétence en étoiles. L’évaluation détaillée (Likert) se déplie pour affiner
                    votre profil.
                  </p>
                  {PROFILE_SYNTHESIS_COMPETENCIES.map((comp) => {
                    const avg = synthesisCompetencyAverage(likert, comp.key);
                    const likertDim = ENTREPRENEURIAL_PROFILE_DIMENSIONS.find((d) => d.key === comp.likertKey);
                    const openDetail = expandedDim === comp.key;
                    return (
                      <div key={comp.key} className="as-skill-row">
                        <div>
                          <button
                            type="button"
                            className="as-link"
                            style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--ink)' }}
                            onClick={() => setExpandedDim(openDetail ? null : comp.key)}
                          >
                            {comp.title}
                          </button>
                          <p className="as-skill-desc">{comp.description}</p>
                        </div>
                        <StarRating
                          value={avg}
                          disabled={profilLocked}
                          onChange={profilLocked ? undefined : (n) => setDimensionScore(comp.likertKey, n)}
                        />
                        <span className="as-skill-score">{avg > 0 ? avg.toFixed(1) : '—'}</span>
                        {openDetail && likertDim ? (
                          <div className="as-skill-detail">
                            <p className="as-skill-detail-label">Affiner — questionnaire Likert</p>
                            {likertDim.questions.map((q) => (
                              <div className="as-q-row" key={q.id}>
                                <p>{q.label}</p>
                                <StarRating
                                  value={likert[q.id] || 0}
                                  disabled={profilLocked}
                                  onChange={
                                    profilLocked
                                      ? undefined
                                      : (n) => setLikert({ ...likert, [q.id]: n })
                                  }
                                />
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </section>

                <aside className="as-profil-col">
                  <h4>Résultat de votre profil</h4>
                  <div className="as-result-score">
                    <p className="eyebrow">Score global</p>
                    <ProgressRing value={liveScore || profileScore} size="lg" color="#22c55e" />
                    <p className="as-result-level">{profileLabel}</p>
                  </div>
                  <div className="as-result-block forces">
                    <h5>Forces</h5>
                    {strengths.length === 0 ? (
                      <p className="muted" style={{ margin: 0, fontSize: '0.9rem' }}>
                        Complétez l’évaluation pour voir vos forces.
                      </p>
                    ) : (
                      <ul>
                        {strengths.map((s) => (
                          <li key={s.key}>{s.title}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="as-result-block gaps">
                    <h5>Axes d’amélioration</h5>
                    {gaps.length === 0 ? (
                      <p className="muted" style={{ margin: 0, fontSize: '0.9rem' }}>
                        —
                      </p>
                    ) : (
                      <ul>
                        {gaps.map((s) => (
                          <li key={s.key}>{s.title}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </aside>
              </div>

              <div className="as-profil-actions">
                {!profilLocked ? (
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={loading}
                    onClick={() => void submitProfile()}
                  >
                    Valider mon profil
                  </button>
                ) : (
                  <p className="muted" style={{ marginBottom: 0 }}>
                    <span className="chip">Profil {profileLabel} — {profileScore}%</span>
                    {acc?.profile_summary ? ` ${acc.profile_summary}` : null}
                  </p>
                )}
              </div>
            </div>
          ) : null}

          {view === 'engagement' ? (
            <div className="as-engage">
              <div className="as-engage-hero">
                <div>
                  <h3 className="as-engage-title">Engagement et Résultats Attendus</h3>
                  <p className="muted" style={{ margin: 0 }}>
                    Choisissez votre formule d’accompagnement et vos résultats attendus
                  </p>
                </div>
                <div className="as-engage-illus" aria-hidden>
                  ◎
                </div>
              </div>

              <div className="as-formula-grid">
                {AS_FORMULAS.map((f) => {
                  const selected = formulaKey === f.key;
                  return (
                    <article
                      key={f.key}
                      className={`as-formula-card tone-${f.tone} ${selected ? 'is-selected' : ''}`}
                    >
                      <header>
                        <h4>{f.label}</h4>
                        <p className="as-formula-tagline">{f.tagline}</p>
                      </header>
                      <ul className="as-formula-features">
                        {f.features.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                      <p className="as-formula-duration">Durée : {f.duration}</p>
                      <div className="as-formula-results">
                        <strong>Résultats attendus</strong>
                        <ul>
                          {f.expectedResults.map((item) => (
                            <li key={item}>{item}</li>
                          ))}
                        </ul>
                      </div>
                      <button
                        type="button"
                        className="btn as-formula-pick"
                        disabled={Boolean(acc?.engaged_at)}
                        onClick={() => {
                          setFormulaKey(f.key);
                          setExpectedResults(f.expectedResults.join('\n'));
                        }}
                      >
                        {selected ? 'Formule sélectionnée' : 'Choisir cette formule'}
                      </button>
                    </article>
                  );
                })}
              </div>

              {!acc?.engaged_at ? (
                <div className="as-engage-footer">
                  <label className="as-engage-ack">
                    <input
                      type="checkbox"
                      checked={engageAck}
                      onChange={(e) => setEngageAck(e.target.checked)}
                    />
                    <span>Je m’engage à suivre activement le programme d’accompagnement choisi.</span>
                  </label>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={loading || !acc || acc.status === 'profiling'}
                    onClick={() => void confirmEngagement()}
                  >
                    Valider mon engagement
                  </button>
                </div>
              ) : (
                <p className="muted" style={{ marginBottom: 0 }}>
                  <span className="chip">Engagement acté</span>
                  {' '}
                  le {formatDateFr(acc.engaged_at)} — {formula?.label}
                </p>
              )}
              {acc?.status === 'profiling' ? (
                <p className="muted">Complétez d’abord votre profil entrepreneurial.</p>
              ) : null}
            </div>
          ) : null}

          {view === 'feuille' ? (
            <RoadmapPanel
              formulaKey={acc?.formula_key}
              formulaLabel={formula?.label}
              roadmap={roadmap}
              coachName={coachName}
              validated={Boolean(acc?.roadmap_validated_at)}
              onAccessStep={() => setView('activites')}
            />
          ) : null}

          {view === 'activites' ? (
            !acc ? (
              <div className="as-detail-card">
                <h3 style={{ marginTop: 0 }}>Activités d’Accompagnement</h3>
                <p className="muted">
                  Complétez votre profil entrepreneurial et formalisez votre engagement pour accéder aux activités.
                </p>
              </div>
            ) : (
              <>
                {!activitiesReady ? (
                  <p className="muted" style={{ marginBottom: '1rem' }}>
                    {acc.status === 'engaged' || acc.status === 'roadmap'
                      ? 'Votre conseiller finalise la feuille de route. Le suivi des activités s’activera une fois celle-ci validée.'
                      : 'Le plan d’activités sera disponible après validation de votre feuille de route.'}
                  </p>
                ) : null}
                <ActivitiesPanel
                  accompanimentId={acc.id}
                  entrepreneurName={displayName}
                  formulaKey={acc.formula_key}
                  roadmap={roadmap}
                  activities={activities}
                  onChange={load}
                  mode="user"
                  journeyStatusLabel={
                    acc.status === 'completed'
                      ? 'Terminé'
                      : acc.status === 'evaluation'
                        ? 'Évaluation'
                        : activitiesReady
                          ? 'En cours'
                          : 'En préparation'
                  }
                />
              </>
            )
          ) : null}

          {view === 'evaluation' ? (
            <div className="as-detail-card">
              <h3 style={{ marginTop: 0 }}>Évaluation & Résultats</h3>
              {!acc ? (
                <p className="muted">
                  Complétez les étapes précédentes de votre parcours pour accéder à l’évaluation finale.
                </p>
              ) : !evaluationReady && !acc.evaluation ? (
                <p className="muted">
                  {acc.status === 'activities'
                    ? 'L’évaluation sera ouverte à la fin de vos activités d’accompagnement, avec votre conseiller.'
                    : 'L’évaluation sera renseignée avec votre conseiller une fois les activités terminées.'}
                </p>
              ) : !acc.evaluation ? (
                <p className="muted">L’évaluation sera renseignée avec votre conseiller.</p>
              ) : (
                <div>
                  <p>
                    Progression : <strong>{acc.evaluation.progress_score}/5</strong> — Impact :{' '}
                    <strong>{acc.evaluation.impact_score}/5</strong> — Satisfaction :{' '}
                    <strong>{acc.evaluation.satisfaction_score}/5</strong>
                  </p>
                  {acc.evaluation.results_summary ? (
                    <p>
                      <strong>Synthèse :</strong> {acc.evaluation.results_summary}
                    </p>
                  ) : null}
                  {acc.evaluation.strengths ? (
                    <p>
                      <strong>Points forts :</strong> {acc.evaluation.strengths}
                    </p>
                  ) : null}
                  {acc.evaluation.gaps ? (
                    <p>
                      <strong>Écarts :</strong> {acc.evaluation.gaps}
                    </p>
                  ) : null}
                  {acc.evaluation.next_steps ? (
                    <p>
                      <strong>Suite :</strong> {acc.evaluation.next_steps}
                    </p>
                  ) : null}
                </div>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
